import { IconButton } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import TimelineIcon from '@material-ui/icons/Timeline';
import clsx from 'clsx';
import { isNumber, min, round } from 'lodash';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useValuationSettings } from 'src/hooks/useValuationSettings';
import { getCountryCodeFromValuationCountry } from 'src/modules/country-helper';
import {
  CountryCode,
  DetailCarPreviousAuctionDTO,
  MarketingChannel,
  MonetaryAmount,
  ValuationCountryCode,
} from 'src/modules/generated/api';

import { DeltaChip } from './DeltaChip';
import { TrendChart } from './TrendChart';

export type TrendCellProps = {
  prevAuctions: Array<DetailCarPreviousAuctionDTO>;
  currPrice?: number;
  potential?: Array<MonetaryAmount>;
  marketingChannel: MarketingChannel[];
};

const useStyles = makeStyles((theme) => ({
  styling: {
    position: 'absolute',
    left: '40px',
    bottom: '7.5px',
  },

  btn: {
    position: 'absolute',
    left: '5px',
    bottom: '5px',
    zIndex: 1,
    color: grey[900],
  },

  btnDiagramOff: {
    color: theme.palette.primary.main,
  },
}));

/**
 * Reference country for the price trend (only auctions visible for it are compared).
 * The logic has changed several times, keep this history up to date:
 * - IRIS-4768 (2025-06): DE only compares DE auctions. All other countries buy indirectly via
 *   the AT accounts, so they see the AT trend.
 * - IRIS-6730 (2026-10): the default for all countries besides DE switched from AT to Alluc (EU),
 *   because auctions shown only to the Alluc account were missing from the trend.
 */
export const getTrendReferenceCountry = (valuationCountry: ValuationCountryCode | null): CountryCode =>
  getCountryCodeFromValuationCountry(valuationCountry) === CountryCode.De ? CountryCode.De : CountryCode.Eu;

/**
 * Formula: (current price - lowest previous price) / lowest previous price, in percent rounded to
 * one decimal. No distinction between bid and fixed price, packages are excluded.
 * The first relevant auction is treated as the current one.
 */
export const getPriceTrend = (prevAuctions: DetailCarPreviousAuctionDTO[], referenceCountry: CountryCode) => {
  const relevantAuctions = prevAuctions.filter(
    (auction) => auction.offerType === 'INDIVIDUAL' && auction.visibleForCountry?.includes(referenceCountry),
  );
  const auctionPrices = relevantAuctions.map((auction) => auction.price?.base?.amount);
  const currPrice = auctionPrices[0];
  if (auctionPrices.length > 1) auctionPrices.shift();

  const minAuctionPrice = min(auctionPrices);
  const diff = currPrice && minAuctionPrice ? round(((currPrice - minAuctionPrice) / minAuctionPrice) * 100, 1) : false;

  return { diff, hasPriceChange: !auctionPrices.every((item) => item === currPrice) };
};

export const TrendCell = ({ prevAuctions, potential, marketingChannel }: TrendCellProps) => {
  const classes = useStyles();
  const { valuationCountry: globalValuationCountry } = useValuationSettings();

  const [showLines, setShowLines] = useState(false);

  const handleClick = () => {
    setShowLines(!showLines);
  };

  const { diff, hasPriceChange } = getPriceTrend(prevAuctions, getTrendReferenceCountry(globalValuationCountry));

  return (
    <div>
      {hasPriceChange ? (
        <>
          {prevAuctions.length >= 2 ? (
            <IconButton
              style={{
                marginRight: '10px',
              }}
              size="small"
              className={showLines ? clsx(classes.btn) : clsx(classes.btnDiagramOff, classes.btn)}
              onClick={() => {
                handleClick();
              }}
            >
              <TimelineIcon fontSize="small" />
            </IconButton>
          ) : null}

          {showLines ? (
            <TrendChart
              prevAuctions={prevAuctions || []}
              monetary={potential || []}
              diffNegative={isNumber(diff) ? diff < 0 : false}
              marketingChannel={marketingChannel}
            />
          ) : (
            isNumber(diff) &&
            diff !== 0 && (
              <DeltaChip
                className={classes.styling}
                value={diff}
                label={`${diff} %`}
                tooltip={<Trans i18nKey="car.prices.comparedToLowestBasePrice" />}
              />
            )
          )}
        </>
      ) : null}
    </div>
  );
};
